/* SOLEVIA — UI widgets for layout/solevia.liquid: gallery, tabs, accordion, carousel, marquee, stories.
   Loaded after solevia.js and uses its helpers on window.Solevia. */
(() => {
  'use strict';
  const S = window.Solevia;
  if (!S || S.__uiLoaded) return;
  S.__uiLoaded = true;
  const reduced = S.reduced, designMode = S.designMode, define = S.define, lock = S.lock, unlock = S.unlock;

  /* ---------- Gallery ---------- */
  class SvGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-sv-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-sv-thumb]'));
      this.stage = this.querySelector('[data-sv-stage]');
      this.count = this.querySelector('[data-sv-count]');
      this.lb = this.querySelector('[data-sv-lightbox]');
      this.i = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.onClick = (e) => {
        if (e.target.closest('[data-sv-prev]')) { this.go(this.i - 1); return; }
        if (e.target.closest('[data-sv-next]')) { this.go(this.i + 1); return; }
        const th = e.target.closest('[data-sv-thumb]');
        if (th) { this.go(parseInt(th.dataset.svThumb, 10)); return; }
        if (e.target.closest('[data-sv-zoom]')) { this.openLb(e.target.closest('[data-sv-zoom]')); return; }
        if (e.target.closest('[data-sv-lb-close]') || e.target === this.lb) { this.closeLb(); return; }
        if (e.target.closest('[data-sv-lb-prev]')) { this.go(this.i - 1); this.fillLb(); return; }
        if (e.target.closest('[data-sv-lb-next]')) { this.go(this.i + 1); this.fillLb(); }
      };
      this.onKey = (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        if (!(e.target === this.stage || (this.lb && this.lb.open))) return;
        e.preventDefault();
        this.go(this.i + (e.key === 'ArrowRight' ? 1 : -1));
        if (this.lb && this.lb.open) this.fillLb();
      };
      this.onTouchStart = (e) => { this.tx = e.changedTouches[0].clientX; this.ty = e.changedTouches[0].clientY; };
      this.onTouchEnd = (e) => {
        const dx = e.changedTouches[0].clientX - (this.tx || 0);
        const dy = e.changedTouches[0].clientY - (this.ty || 0);
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { this.go(this.i + (dx < 0 ? 1 : -1)); if (this.lb && this.lb.open) this.fillLb(); }
      };
      this.onMedia = (e) => {
        if (!e.detail || e.detail.sectionId !== this.dataset.sectionId) return;
        const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(e.detail.mediaId));
        if (idx >= 0) this.go(idx);
      };
      this.onLbCancel = (e) => { e.preventDefault(); this.closeLb(); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      document.addEventListener('sv:media:select', this.onMedia);
      this.onLbClose = () => {
        if (this.lbDone) return;
        this.lbDone = true;
        unlock('lightbox-' + this.dataset.sectionId);
        if (this.stage) this.stage.focus({ preventScroll: true });
        else if (this.lbOpener && this.lbOpener.isConnected) this.lbOpener.focus({ preventScroll: true });
      };
      if (this.lb) { this.lb.addEventListener('cancel', this.onLbCancel); this.lb.addEventListener('close', this.onLbClose); }
      this.go(this.i, true);
    }
    disconnectedCallback() {
      document.removeEventListener('sv:media:select', this.onMedia);
      if (this.lb) { this.lb.removeEventListener('cancel', this.onLbCancel); this.lb.removeEventListener('close', this.onLbClose); }
      unlock('lightbox-' + this.dataset.sectionId);
    }
    go(n, silent) {
      const total = this.slides.length;
      if (!total) return;
      this.i = (n + total) % total;
      this.slides.forEach((s, k) => {
        const on = k === this.i;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        s.inert = !on;
        if (!on) s.querySelectorAll('video').forEach((vid) => vid.pause());
      });
      this.thumbs.forEach((th, k) => th.setAttribute('aria-current', k === this.i ? 'true' : 'false'));
      // Thumbnails sit in one scrolling row: keep the active one in view (scrolls the row only, never the page).
      const row = this.thumbs[this.i] && this.thumbs[this.i].parentElement;
      if (row && row.scrollWidth > row.clientWidth) {
        const a = this.thumbs[this.i].getBoundingClientRect(), r = row.getBoundingClientRect();
        if (a.left < r.left || a.right > r.right) row.scrollBy({ left: (a.left + a.width / 2) - (r.left + r.width / 2), behavior: reduced() ? 'auto' : 'smooth' });
      }
      if (this.count) this.count.textContent = (this.i + 1) + ' / ' + total;
      if (!silent) {
        const live = this.querySelector('[data-sv-gallery-live]');
        if (live) live.textContent = (live.dataset.template || '').replace('[n]', this.i + 1).replace('[total]', total);
      }
    }
    fillLb() {
      if (!this.lb) return;
      const frame = this.lb.querySelector('[data-sv-lb-frame]');
      const cap = this.lb.querySelector('[data-sv-lb-caption]');
      const slide = this.slides[this.i];
      if (!frame || !slide) return;
      const src = slide.dataset.zoomSrc;
      frame.innerHTML = '';
      if (src) {
        const img = document.createElement('img');
        img.src = src; img.alt = slide.dataset.alt || '';
        frame.appendChild(img);
      } else {
        const art = slide.querySelector('.sv-art');
        if (art) frame.appendChild(art.cloneNode(true));
      }
      if (cap) cap.textContent = (slide.dataset.caption ? slide.dataset.caption + ' · ' : '') + (this.i + 1) + ' / ' + this.slides.length;
    }
    openLb(opener) {
      if (!this.lb) return;
      this.lbOpener = opener;
      this.lbDone = false;
      this.fillLb();
      try { this.lb.showModal(); } catch (e) { this.lb.setAttribute('open', ''); }
      lock('lightbox-' + this.dataset.sectionId);
      const c = this.lb.querySelector('[data-sv-lb-close]');
      if (c) c.focus({ preventScroll: true });
    }
    closeLb() {
      if (!this.lb || !this.lb.open) return;
      this.lb.close();
      this.onLbClose();
    }
  }

  /* ---------- Tabs (automatic activation, roving tabindex) ---------- */
  class SvTabs extends HTMLElement {
    connectedCallback() {
      this.onClick = (e) => { const tab = e.target.closest('[role="tab"]'); if (tab && this.contains(tab)) this.select(tab); };
      this.onKey = (e) => {
        const tab = e.target.closest('[role="tab"]'); if (!tab) return;
        const tabs = this.tabs(); const i = tabs.indexOf(tab); let j = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') j = 0; else if (e.key === 'End') j = tabs.length - 1;
        if (j === null) return;
        e.preventDefault(); this.select(tabs[j]); tabs[j].focus();
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
    }
    tabs() { return Array.from(this.querySelectorAll('[role="tab"]')); }
    select(tab) {
      this.tabs().forEach((x) => {
        const on = x === tab;
        x.setAttribute('aria-selected', on ? 'true' : 'false');
        x.tabIndex = on ? 0 : -1;
        const p = document.getElementById(x.getAttribute('aria-controls'));
        if (p) {
          const was = p.hidden; p.hidden = !on;
          if (on && was && !reduced()) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); }
        }
      });
    }
  }

  /* ---------- Accordion ---------- */
  class SvAccordion extends HTMLElement {
    connectedCallback() {
      this.onClick = (e) => {
        const b = e.target.closest('[data-sv-acc-btn]');
        if (!b || !this.contains(b)) return;
        this.toggle(b, b.getAttribute('aria-expanded') !== 'true');
      };
      this.addEventListener('click', this.onClick);
    }
    toggle(btn, open) {
      if (open && this.hasAttribute('data-single')) {
        this.querySelectorAll('[data-sv-acc-btn][aria-expanded="true"]').forEach((b) => { if (b !== btn) this.toggle(b, false); });
      }
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (panel) { if (open) panel.removeAttribute('data-collapsed'); else panel.setAttribute('data-collapsed', ''); }
      const item = btn.closest('[data-sv-acc-item]');
      if (item) item.classList.toggle('is-open', open);
    }
    openById(id) {
      const b = this.querySelector('[data-sv-acc-btn][aria-controls="' + id + '"]');
      if (b) { this.toggle(b, true); return b; }
      return null;
    }
  }

  /* ---------- Scroll-snap carousel ---------- */
  class SvCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sv-track]');
      this.prev = this.querySelector('[data-sv-car-prev]');
      this.next = this.querySelector('[data-sv-car-next]');
      if (!this.track) return;
      this.update = this.update.bind(this);
      this.onScroll = () => { if (this.raf) return; this.raf = window.requestAnimationFrame(() => { this.raf = 0; this.update(); }); };
      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      if ('ResizeObserver' in window) { this.ro = new ResizeObserver(this.update); this.ro.observe(this.track); }
      this.update();
    }
    disconnectedCallback() { if (this.ro) this.ro.disconnect(); }
    step(dir) { this.track.scrollBy({ left: dir * this.track.clientWidth * 0.8, behavior: reduced() ? 'auto' : 'smooth' }); }
    update() {
      const tr = this.track;
      if (this.prev) this.prev.disabled = tr.scrollLeft <= 2;
      if (this.next) this.next.disabled = tr.scrollLeft + tr.clientWidth >= tr.scrollWidth - 4;
    }
    reveal(el) { if (el && this.track.contains(el)) this.track.scrollTo({ left: el.offsetLeft - this.track.offsetLeft, behavior: 'auto' }); }
  }

  /* ---------- Marquee (pausable, static when reduced motion / editor) ---------- */
  class SvMarquee extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.sv-marquee');
      this.viewport = this.querySelector('.sv-marquee__viewport');
      this.btn = this.querySelector('[data-sv-marquee-toggle]');
      const still = reduced() || designMode();
      if (still) {
        this.querySelectorAll('[data-sv-marquee-clone]').forEach((c) => { c.hidden = true; });
        if (this.viewport) this.viewport.classList.add('is-static');
        if (this.btn) this.btn.hidden = true;
        return;
      }
      if (this.section) this.section.classList.add('is-animated');
      this.onEnter = () => this.section && this.section.classList.add('is-paused');
      this.onLeave = () => { if (this.section && !this.userPaused) this.section.classList.remove('is-paused'); };
      this.onBtn = () => {
        this.userPaused = !this.userPaused;
        this.section.classList.toggle('is-paused', this.userPaused);
        this.btn.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
        this.btn.textContent = this.userPaused ? this.btn.dataset.play : this.btn.dataset.pause;
      };
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
      if (this.btn) this.btn.addEventListener('click', this.onBtn);
    }
  }

  /* ---------- Stories (one-at-a-time editorial slides) ---------- */
  class SvStories extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-sv-story]'));
      this.dots = Array.from(this.querySelectorAll('[data-sv-story-dot]'));
      this.counter = this.querySelector('[data-sv-story-count]');
      this.i = 0;
      this.onClick = (e) => {
        if (e.target.closest('[data-sv-story-prev]')) this.go(this.i - 1);
        else if (e.target.closest('[data-sv-story-next]')) this.go(this.i + 1);
        else { const d = e.target.closest('[data-sv-story-dot]'); if (d) this.go(parseInt(d.dataset.svStoryDot, 10)); }
      };
      this.addEventListener('click', this.onClick);
      this.go(0, true);
    }
    go(n, silent) {
      const total = this.items.length; if (!total) return;
      this.i = (n + total) % total;
      this.items.forEach((it, k) => {
        const on = k === this.i;
        const was = it.hidden;
        it.hidden = !on;
        if (on && was && !silent && !reduced()) { it.classList.remove('is-entering'); void it.offsetWidth; it.classList.add('is-entering'); }
      });
      this.dots.forEach((d, k) => d.setAttribute('aria-current', k === this.i ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = (this.i + 1) + ' / ' + total;
    }
  }

  define('sv-gallery', SvGallery);
  define('sv-tabs', SvTabs);
  define('sv-accordion', SvAccordion);
  define('sv-carousel', SvCarousel);
  define('sv-marquee', SvMarquee);
  define('sv-stories', SvStories);
})();
